import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import CinematicBackground from "@/components/CinematicBackground";
import { useApp } from "@/context/AppContext";
import { useLang } from "@/i18n/LanguageContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const STR = {
  ar: {
    title: "الرؤية السينمائية",
    sub: "CineVision AI",
    intro: "صوّر أو ارفع صورة/فيديو قصير لمشهد حقيقي، ثم دع الذكاء الاصطناعي يقيّمه ويوضّح كيف يصبح أكثر سينمائية.",
    privacy: "خصوصية: تُعالَج الوسائط مؤقتاً فقط ولا تُحفظ بشكل دائم.",
    pick: "اختر صورة أو فيديو",
    capture: "التقاط بالكاميرا",
    upload: "رفع من المعرض",
    takePhoto: "التقاط الصورة",
    flip: "تبديل الكاميرا",
    closeCam: "إغلاق",
    camHint: "وجّه الكاميرا نحو الشخص أو المشهد ثم اضغط التقاط",
    errCam: "تعذّر الوصول إلى الكاميرا، تحقق من الأذونات",
    change: "تغيير الوسيط",
    analyze: "تحليل المشهد",
    analyzing: "جارٍ التحليل…",
    original: "المشهد الأصلي",
    proposed: "الرؤية السينمائية المقترحة",
    scores: "الدرجات الإرشادية",
    overall: "التقييم السينمائي العام",
    composition: "التكوين",
    lighting: "الإضاءة",
    camera_angle: "زاوية الكاميرا",
    depth: "العمق البصري",
    gen: "إنشاء التصور السينمائي",
    genning: "جارٍ إنشاء الصورة…",
    genNote: "صورة تصوّر بصري مقترح بالذكاء الاصطناعي — قد لا تطابق ملامح الأشخاص بدقة 100%.",
    compare: "المشهد الأصلي | الرؤية السينمائية",
    slide: "اسحب للمقارنة",
    errVideo: "تعذّر قراءة الفيديو",
    errAnalyze: "فشل التحليل، حاول مجدداً",
    errGen: "فشل إنشاء الصورة، حاول مجدداً",
    director: "المخرج الذكي",
    directorRun: "استشر المخرج الذكي",
    directorRunning: "المخرج يفكّر…",
    storyboard: "لوحة القصة",
    storyboardRun: "أنشئ لوحة القصة",
    storyboardRunning: "جارٍ بناء اللقطات…",
    shotWord: "لقطة",
    movement: "حركة الكاميرا",
    purpose: "الغرض",
    save: "حفظ في المعرض",
    savedOk: "تم الحفظ في معرض الجلسة",
    gallery: "معرض الجلسة",
    galleryEmpty: "لا توجد عناصر محفوظة بعد",
    galleryNote: "يُحفظ داخل الجلسة فقط ويُمسح عند إغلاق التطبيق.",
    close: "إغلاق",
    del: "حذف",
    errDirector: "تعذّر جلب اقتراحات المخرج، حاول مجدداً",
    errStoryboard: "تعذّر بناء لوحة القصة، حاول مجدداً",
    cat: { shot: "نوع اللقطة", movement: "حركة الكاميرا", angle: "الزاوية", lighting: "الإضاءة", lens: "العدسة", composition: "التكوين" },
  },
  en: {
    title: "Cinematic Vision",
    sub: "CineVision AI",
    intro: "Capture or upload a photo/short video of a real scene, then let the AI rate it and show how to make it more cinematic.",
    privacy: "Privacy: media is processed temporarily only and never stored permanently.",
    pick: "Choose a photo or video",
    capture: "Capture with camera",
    upload: "Upload from gallery",
    takePhoto: "Take photo",
    flip: "Flip camera",
    closeCam: "Close",
    camHint: "Point the camera at the person or scene, then tap capture",
    errCam: "Could not access the camera, check permissions",
    change: "Change media",
    analyze: "Analyze scene",
    analyzing: "Analyzing…",
    original: "Original scene",
    proposed: "Proposed cinematic vision",
    scores: "Guidance scores",
    overall: "Overall cinematic rating",
    composition: "Composition",
    lighting: "Lighting",
    camera_angle: "Camera angle",
    depth: "Visual depth",
    gen: "Generate cinematic vision",
    genning: "Generating image…",
    genNote: "An AI-proposed visual impression — it may not match people's features 100%.",
    compare: "Original | Cinematic vision",
    slide: "Drag to compare",
    errVideo: "Could not read the video",
    errAnalyze: "Analysis failed, please retry",
    errGen: "Image generation failed, please retry",
    director: "AI Director",
    directorRun: "Ask the AI Director",
    directorRunning: "Director is thinking…",
    storyboard: "Storyboard",
    storyboardRun: "Build storyboard",
    storyboardRunning: "Building shots…",
    shotWord: "Shot",
    movement: "Camera movement",
    purpose: "Purpose",
    save: "Save to gallery",
    savedOk: "Saved to session gallery",
    gallery: "Session gallery",
    galleryEmpty: "No saved items yet",
    galleryNote: "Stored only for this session and cleared when the app closes.",
    close: "Close",
    del: "Delete",
    errDirector: "Could not get director suggestions, please retry",
    errStoryboard: "Could not build the storyboard, please retry",
    cat: { shot: "Shot type", movement: "Camera movement", angle: "Angle", lighting: "Lighting", lens: "Lens", composition: "Composition" },
  },
};

const MARKER_ICON = { arrow: "fa-arrow-right-long", point: "fa-location-dot", light: "fa-sun", lookspace: "fa-arrows-left-right", focus: "fa-crosshairs" };
const MARKER_COLOR = { arrow: "#4ea8ff", point: "#f2c66b", light: "#ffd76a", lookspace: "#7bd88f", focus: "#e78fa3" };
const CAT_ICON = { shot: "fa-film", movement: "fa-arrows-up-down-left-right", angle: "fa-video", lighting: "fa-lightbulb", lens: "fa-camera", composition: "fa-table-cells" };

function ScoreBar({ label, value, color }) {
  return (
    <div data-testid={`score-${label}`}>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-300">{label}</span>
        <span className="font-display" style={{ color }}>{value}/100</span>
      </div>
      <div className="h-2 rounded-full bg-white/8 overflow-hidden">
        <motion.div className="h-full rounded-full" style={{ background: color }} initial={{ width: 0 }} animate={{ width: `${value}%` }} transition={{ duration: 0.8 }} />
      </div>
    </div>
  );
}

export default function CineVision() {
  const { lang } = useLang();
  const { cvGallery, addToGallery, removeFromGallery } = useApp();
  const rtl = lang === "ar";
  const L = STR[lang] || STR.ar;
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [original, setOriginal] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [analyzedFor, setAnalyzedFor] = useState(null);
  const [slider, setSlider] = useState(50);
  const [camOpen, setCamOpen] = useState(false);
  const [facing, setFacing] = useState("environment");
  const [director, setDirector] = useState(null);
  const [dirLoading, setDirLoading] = useState(false);
  const [storyboard, setStoryboard] = useState(null);
  const [sbLoading, setSbLoading] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);

  const startStream = async (mode) => {
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      toast.error(L.errCam);
      setCamOpen(false);
    }
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    if (camOpen) startStream(facing);
    return () => { if (!camOpen) stopStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camOpen, facing]);

  useEffect(() => () => stopStream(), []);

  const openCamera = () => { setResult(null); setGenerated(null); setAnalyzedFor(null); setDirector(null); setStoryboard(null); setCamOpen(true); };
  const closeCamera = () => { stopStream(); setCamOpen(false); };
  const flipCamera = () => setFacing((f) => (f === "environment" ? "user" : "environment"));

  const takePhoto = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    if (facing === "user") { ctx.translate(c.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(v, 0, 0, c.width, c.height);
    const data = c.toDataURL("image/jpeg", 0.9);
    stopStream();
    setCamOpen(false);
    setSessionId(`cv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    setOriginal(data);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setResult(null); setGenerated(null); setAnalyzedFor(null); setDirector(null); setStoryboard(null);
    setSessionId(`cv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    if (f.type.startsWith("video/")) {
      try {
        const url = URL.createObjectURL(f);
        const v = document.createElement("video");
        v.src = url; v.muted = true; v.playsInline = true;
        await new Promise((res, rej) => { v.onloadeddata = res; v.onerror = rej; });
        v.currentTime = Math.min(1, (v.duration || 2) / 2);
        await new Promise((res) => { v.onseeked = res; });
        const c = document.createElement("canvas");
        c.width = v.videoWidth || 640; c.height = v.videoHeight || 360;
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        URL.revokeObjectURL(url);
        setOriginal(c.toDataURL("image/jpeg", 0.85));
      } catch (err) {
        toast.error(L.errVideo);
      }
    } else {
      const reader = new FileReader();
      reader.onload = () => setOriginal(reader.result);
      reader.readAsDataURL(f);
    }
  };

  const analyze = async () => {
    if (!original || loading) return;
    if (result && analyzedFor === original) return; // cache within session
    setLoading(true); setGenerated(null);
    try {
      const r = await fetch(`${API}/api/cinevision/analyze`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_base64: original, session_id: sessionId }),
      });
      if (!r.ok) throw new Error("bad");
      const data = await r.json();
      setResult(data); setAnalyzedFor(original);
    } catch (e) {
      toast.error(L.errAnalyze);
    } finally { setLoading(false); }
  };

  const generate = async () => {
    if (!original || genLoading) return;
    setGenLoading(true);
    try {
      const r = await fetch(`${API}/api/cinevision/generate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_base64: original, session_id: sessionId }),
      });
      if (!r.ok) throw new Error("bad");
      const data = await r.json();
      if (!data.image_base64) throw new Error("noimg");
      setGenerated(`data:${data.mime_type || "image/png"};base64,${data.image_base64}`);
      setSlider(50);
    } catch (e) {
      toast.error(L.errGen);
    } finally { setGenLoading(false); }
  };

  const runDirector = async () => {
    if (!original || dirLoading) return;
    setDirLoading(true);
    try {
      const r = await fetch(`${API}/api/cinevision/director`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_base64: original, session_id: sessionId }),
      });
      if (!r.ok) throw new Error("bad");
      setDirector(await r.json());
    } catch (e) { toast.error(L.errDirector); } finally { setDirLoading(false); }
  };

  const runStoryboard = async () => {
    if (!original || sbLoading) return;
    setSbLoading(true);
    try {
      const r = await fetch(`${API}/api/cinevision/storyboard`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_base64: original, session_id: sessionId }),
      });
      if (!r.ok) throw new Error("bad");
      setStoryboard(await r.json());
    } catch (e) { toast.error(L.errStoryboard); } finally { setSbLoading(false); }
  };

  const saveToGallery = () => {
    if (!result) return;
    addToGallery({
      thumb: original,
      generated: generated || null,
      overall: result.overall,
      scores: result.scores,
      summary: result.summary,
      savedAt: new Date().toISOString(),
    });
    toast.success(L.savedOk);
  };

  return (
    <div className="relative px-4 pt-8" dir={rtl ? "rtl" : "ltr"} data-testid="cinevision-page">
      <CinematicBackground />

      <header className="mb-4">
        <p className="font-display tracking-[0.25em] text-ai text-xs mb-1">{L.sub}</p>
        <h1 className="font-display text-2xl gold-gradient-text">{L.title}</h1>
        <p className="text-sm text-slate-400 mt-2">{L.intro}</p>
        <p className="text-[11px] text-slate-500 mt-1"><i className="fa-solid fa-lock mr-1" /> {L.privacy}</p>
        <button onClick={() => setGalleryOpen(true)} data-testid="cinevision-gallery-btn"
          className="mt-3 inline-flex items-center gap-2 glass rounded-full px-4 py-2 text-slate-200 text-xs">
          <i className="fa-solid fa-bookmark text-emerald-300" />
          <span className="font-display">{L.gallery}</span>
          {cvGallery.length > 0 && (
            <span className="min-w-5 h-5 px-1.5 rounded-full bg-emerald-400/20 text-emerald-200 text-[11px] flex items-center justify-center" data-testid="cinevision-gallery-count">{cvGallery.length}</span>
          )}
        </button>
      </header>

      {/* media picker */}
      <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={onFile} data-testid="cinevision-file-input" />

      {camOpen && (
        <div className="space-y-3" data-testid="cinevision-camera">
          <div className="relative rounded-3xl overflow-hidden glass" style={{ background: "#04060d" }}>
            <video ref={videoRef} autoPlay playsInline muted
              className="w-full block" style={{ transform: facing === "user" ? "scaleX(-1)" : "none", maxHeight: "60vh", objectFit: "cover" }} />
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/50 rounded-full px-3 py-1.5 backdrop-blur">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[11px] text-slate-200">{L.sub}</span>
            </div>
            <button onClick={flipCamera} data-testid="cinevision-flip-btn"
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 backdrop-blur text-slate-100 flex items-center justify-center" title={L.flip}>
              <i className="fa-solid fa-camera-rotate" />
            </button>
            <p className="absolute bottom-16 left-1/2 -translate-x-1/2 text-[11px] text-slate-200 bg-black/50 px-3 py-1 rounded-full backdrop-blur whitespace-nowrap">{L.camHint}</p>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4">
              <button onClick={closeCamera} data-testid="cinevision-cam-close-btn"
                className="px-4 py-2 rounded-full bg-black/50 backdrop-blur text-slate-200 text-sm">{L.closeCam}</button>
              <button onClick={takePhoto} data-testid="cinevision-take-photo-btn"
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: "linear-gradient(90deg,#f4d78a,#d9b46a)" }}>
                <i className="fa-solid fa-camera text-navy text-xl" />
              </button>
              <span className="w-[52px]" />
            </div>
          </div>
        </div>
      )}

      {!camOpen && !original && (
        <div className="grid grid-cols-2 gap-3" data-testid="cinevision-pickers">
          <button onClick={openCamera} data-testid="cinevision-capture-btn"
            className="glass rounded-3xl py-8 flex flex-col items-center gap-3 text-gold border border-dashed" style={{ borderColor: "rgba(217,180,106,0.4)" }}>
            <i className="fa-solid fa-camera-retro text-3xl" />
            <span className="font-display text-sm">{L.capture}</span>
          </button>
          <button onClick={() => fileRef.current?.click()} data-testid="cinevision-pick-btn"
            className="glass rounded-3xl py-8 flex flex-col items-center gap-3 text-ai border border-dashed" style={{ borderColor: "rgba(78,168,255,0.4)" }}>
            <i className="fa-solid fa-image text-3xl" />
            <span className="font-display text-sm">{L.upload}</span>
          </button>
        </div>
      )}

      {!camOpen && original && (
        <div className="space-y-4">
          {/* original + overlay */}
          <div className="relative rounded-3xl overflow-hidden glass" data-testid="cinevision-original">
            <img src={original} alt="original scene" className="w-full block" />
            {result?.markers && (
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="cinevision-overlay">
                {result.markers.map((m, i) => (
                  <g key={i}>
                    {m.type === "arrow" && (
                      <line x1={rtl ? m.x * 100 + 10 : m.x * 100 - 10} y1={m.y * 100} x2={m.x * 100} y2={m.y * 100}
                        stroke={MARKER_COLOR.arrow} strokeWidth="0.8" markerEnd="url(#ah)" vectorEffect="non-scaling-stroke" />
                    )}
                  </g>
                ))}
                <defs>
                  <marker id="ah" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 Z" fill="#4ea8ff" />
                  </marker>
                </defs>
              </svg>
            )}
            {result?.markers && (
              <div className="absolute inset-0 pointer-events-none">
                {result.markers.map((m, i) => (
                  <div key={i} data-testid={`marker-${i}`}
                    className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-1"
                    style={{ left: `${m.x * 100}%`, top: `${m.y * 100}%` }}>
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px]"
                      style={{ background: "rgba(4,6,13,0.75)", border: `1px solid ${MARKER_COLOR[m.type] || "#f2c66b"}`, color: MARKER_COLOR[m.type] || "#f2c66b" }}>
                      <i className={`fa-solid ${MARKER_ICON[m.type] || "fa-location-dot"}`} />
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-md whitespace-nowrap"
                      style={{ background: "rgba(4,6,13,0.8)", color: "#e2e8f0" }}>
                      {m.label?.[lang] || m.label?.ar || ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button onClick={() => { setOriginal(null); setResult(null); setGenerated(null); setAnalyzedFor(null); setDirector(null); setStoryboard(null); }} data-testid="cinevision-change-btn"
              className="flex-1 glass rounded-2xl py-3 text-slate-300 text-sm">
              <i className="fa-solid fa-arrows-rotate mr-1" /> {L.change}
            </button>
            <button onClick={analyze} disabled={loading} data-testid="cinevision-analyze-btn"
              className="flex-[2] rounded-2xl py-3 font-display text-navy disabled:opacity-60"
              style={{ background: "linear-gradient(90deg,#f4d78a,#d9b46a)" }}>
              <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-wand-sparkles"} mr-2`} />
              {loading ? L.analyzing : L.analyze}
            </button>
          </div>

          {/* results */}
          {result && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="cinevision-result">
              <div className="glass rounded-3xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-display text-slate-200">{L.overall}</span>
                  <span className="font-display text-2xl gold-gradient-text" data-testid="cinevision-overall">{result.overall}/100</span>
                </div>
                <div className="space-y-3">
                  <ScoreBar label={L.composition} value={result.scores?.composition ?? 0} color="#4ea8ff" />
                  <ScoreBar label={L.lighting} value={result.scores?.lighting ?? 0} color="#ffd76a" />
                  <ScoreBar label={L.camera_angle} value={result.scores?.camera_angle ?? 0} color="#e78fa3" />
                  <ScoreBar label={L.depth} value={result.scores?.depth ?? 0} color="#7bd88f" />
                </div>
                <p className="text-sm text-slate-300 mt-4 leading-relaxed">{result.summary?.[lang] || result.summary?.ar}</p>
                <p className="text-[11px] text-slate-500 mt-2 italic">{result.disclaimer?.[lang] || result.disclaimer?.ar}</p>
              </div>

              {/* action row: director / storyboard / save */}
              <div className="grid grid-cols-3 gap-2" data-testid="cinevision-actions">
                <button onClick={runDirector} disabled={dirLoading} data-testid="cinevision-director-btn"
                  className="glass rounded-2xl py-3 flex flex-col items-center gap-1 text-ai disabled:opacity-60 text-xs">
                  <i className={`fa-solid ${dirLoading ? "fa-spinner fa-spin" : "fa-user-tie"} text-lg`} />
                  <span className="font-display leading-tight text-center">{dirLoading ? L.directorRunning : L.director}</span>
                </button>
                <button onClick={runStoryboard} disabled={sbLoading} data-testid="cinevision-storyboard-btn"
                  className="glass rounded-2xl py-3 flex flex-col items-center gap-1 text-gold disabled:opacity-60 text-xs">
                  <i className={`fa-solid ${sbLoading ? "fa-spinner fa-spin" : "fa-images"} text-lg`} />
                  <span className="font-display leading-tight text-center">{sbLoading ? L.storyboardRunning : L.storyboard}</span>
                </button>
                <button onClick={saveToGallery} data-testid="cinevision-save-btn"
                  className="glass rounded-2xl py-3 flex flex-col items-center gap-1 text-emerald-300 text-xs">
                  <i className="fa-solid fa-bookmark text-lg" />
                  <span className="font-display leading-tight text-center">{L.save}</span>
                </button>
              </div>

              {/* AI Director output */}
              {director?.directives && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-4 space-y-3" data-testid="cinevision-director-result">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-user-tie text-ai" />
                    <span className="font-display text-slate-200">{L.director}</span>
                  </div>
                  {director.summary && <p className="text-sm text-slate-300 leading-relaxed">{director.summary?.[lang] || director.summary?.ar}</p>}
                  <div className="grid sm:grid-cols-2 gap-2">
                    {director.directives.map((d, i) => (
                      <div key={i} data-testid={`director-card-${i}`} className="rounded-2xl p-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(78,168,255,0.18)" }}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="w-7 h-7 rounded-full flex items-center justify-center text-ai" style={{ background: "rgba(78,168,255,0.14)" }}>
                            <i className={`fa-solid ${CAT_ICON[d.category] || "fa-clapperboard"} text-xs`} />
                          </span>
                          <span className="text-xs font-display text-slate-200">{d.title?.[lang] || d.title?.ar || (L.cat[d.category] || "")}</span>
                        </div>
                        <p className="text-[13px] text-slate-300 leading-relaxed">{d.tip?.[lang] || d.tip?.ar}</p>
                      </div>
                    ))}
                  </div>
                  {director.disclaimer && <p className="text-[11px] text-slate-500 italic">{director.disclaimer?.[lang] || director.disclaimer?.ar}</p>}
                </motion.div>
              )}

              {/* Live Storyboard output */}
              {storyboard?.shots && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-4 space-y-3" data-testid="cinevision-storyboard-result">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-images text-gold" />
                    <span className="font-display text-slate-200">{L.storyboard}</span>
                  </div>
                  <div className="space-y-2">
                    {storyboard.shots.map((s, i) => (
                      <div key={i} data-testid={`storyboard-shot-${i}`} className="rounded-2xl p-3 flex gap-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(217,180,106,0.18)" }}>
                        <div className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center font-display text-navy" style={{ background: "linear-gradient(90deg,#f4d78a,#d9b46a)" }}>{s.n ?? i + 1}</div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
                            <span className="text-xs font-display text-gold">{s.shot_type?.[lang] || s.shot_type?.ar}</span>
                            <span className="text-[11px] text-ai"><i className="fa-solid fa-arrows-up-down-left-right mr-1" />{s.movement?.[lang] || s.movement?.ar}</span>
                            {s.duration && <span className="text-[10px] text-slate-400 px-1.5 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.06)" }}><i className="fa-regular fa-clock mr-1" />{s.duration}</span>}
                          </div>
                          <p className="text-[13px] text-slate-300 leading-relaxed">{s.description?.[lang] || s.description?.ar}</p>
                          {s.purpose && <p className="text-[11px] text-slate-500 mt-1"><span className="text-slate-400">{L.purpose}: </span>{s.purpose?.[lang] || s.purpose?.ar}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {storyboard.disclaimer && <p className="text-[11px] text-slate-500 italic">{storyboard.disclaimer?.[lang] || storyboard.disclaimer?.ar}</p>}
                </motion.div>
              )}

              {/* generate cinematic vision */}
              {!generated ? (
                <button onClick={generate} disabled={genLoading} data-testid="cinevision-generate-btn"
                  className="w-full rounded-2xl py-4 font-display disabled:opacity-60"
                  style={{ background: "linear-gradient(90deg, rgba(78,168,255,0.25), rgba(217,180,106,0.25))", border: "1px solid rgba(217,180,106,0.5)", color: "#f2c66b" }}>
                  <i className={`fa-solid ${genLoading ? "fa-spinner fa-spin" : "fa-clapperboard"} mr-2`} />
                  {genLoading ? L.genning : L.gen}
                </button>
              ) : (
                <div className="space-y-2" data-testid="cinevision-compare">
                  <p className="font-display text-slate-200 text-center text-sm">{L.compare}</p>
                  <div className="relative rounded-3xl overflow-hidden glass select-none">
                    <img src={original} alt="original" className="w-full block" />
                    <div className="absolute inset-0 overflow-hidden" style={{ width: `${slider}%` }}>
                      <img src={generated} alt="cinematic vision" className="h-full max-w-none block"
                        style={{ width: `${10000 / slider}%` }} data-testid="cinevision-generated" />
                    </div>
                    <div className="absolute top-0 bottom-0" style={{ left: `${slider}%`, width: 2, background: "#f2c66b" }} />
                    <span className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded-md" style={{ background: "rgba(4,6,13,0.8)", color: "#f2c66b" }}>{L.proposed}</span>
                    <span className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded-md" style={{ background: "rgba(4,6,13,0.8)", color: "#cbd5e1" }}>{L.original}</span>
                  </div>
                  <input type="range" min="0" max="100" value={slider} onChange={(e) => setSlider(+e.target.value)}
                    className="w-full accent-gold" data-testid="cinevision-slider" aria-label={L.slide} />
                  <p className="text-[11px] text-slate-500 text-center">{L.genNote}</p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      )}

      {/* session gallery modal */}
      {galleryOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: "rgba(2,4,10,0.72)", backdropFilter: "blur(6px)" }} data-testid="cinevision-gallery-modal" onClick={() => setGalleryOpen(false)}>
          <div className="glass rounded-3xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-bookmark text-emerald-300" />
                <span className="font-display text-slate-100">{L.gallery}</span>
              </div>
              <button onClick={() => setGalleryOpen(false)} data-testid="cinevision-gallery-close" className="text-slate-400 w-8 h-8 flex items-center justify-center rounded-full glass">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mb-4">{L.galleryNote}</p>
            {cvGallery.length === 0 ? (
              <div className="py-12 text-center text-slate-500" data-testid="cinevision-gallery-empty">
                <i className="fa-regular fa-folder-open text-3xl mb-2 block" />
                <span className="text-sm">{L.galleryEmpty}</span>
              </div>
            ) : (
              <div className="space-y-3">
                {cvGallery.map((g) => (
                  <div key={g.id} data-testid={`gallery-item-${g.id}`} className="rounded-2xl p-3 flex gap-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <img src={g.generated || g.thumb} alt="saved" className="w-20 h-20 object-cover rounded-xl flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-display gold-gradient-text text-lg">{g.overall}/100</span>
                        {g.generated && <span className="text-[10px] text-gold px-1.5 py-0.5 rounded" style={{ background: "rgba(217,180,106,0.14)" }}><i className="fa-solid fa-clapperboard mr-1" />{L.proposed}</span>}
                      </div>
                      <p className="text-[13px] text-slate-300 leading-snug line-clamp-2">{g.summary?.[lang] || g.summary?.ar}</p>
                    </div>
                    <button onClick={() => removeFromGallery(g.id)} data-testid={`gallery-delete-${g.id}`} className="text-slate-500 hover:text-red-400 flex-shrink-0 w-8 h-8 flex items-center justify-center" title={L.del}>
                      <i className="fa-solid fa-trash-can" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
